/**
 * /admin/backups — detailed backup dashboard.
 *
 * Sections:
 *   1. Header actions   → "Save Now" + "Refresh"
 *   2. Health summary   → local + offsite side-by-side cards
 *   3. Local dumps      → table of every dump on disk with per-row download
 *   4. Offsite banner   → red banner + guidance when Tigris is not configured
 */
import { useCallback, useEffect, useState } from "react";
import { api, API_ROOT } from "../lib/api";
import { formatRelative, formatBytes, absoluteTimestamp } from "./backupUtils";

export function BackupsPage() {
  const [health, setHealth] = useState(null);
  const [items, setItems] = useState([]);
  const [checkpoints, setCheckpoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [banner, setBanner] = useState(null); // { kind, text }
  const [purgeConfirmId, setPurgeConfirmId] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [h, l, p] = await Promise.all([
        api.get("/admin/backup/health"),
        api.get("/admin/backup/list"),
        api.get("/admin/backup/pre-destructive"),
      ]);
      setHealth(h.data);
      setItems(l.data.items || []);
      setCheckpoints(p.data.items || []);
    } catch (e) {
      setBanner({
        kind: "err",
        text: `Failed to load backups: ${e?.response?.data?.detail || e.message}`,
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const onSaveNow = useCallback(async () => {
    setSaving(true);
    setBanner(null);
    try {
      const { data } = await api.post("/admin/backup/snapshot-now");
      setBanner({
        kind: "ok",
        text: `Snapshot saved: ${data.name} (${formatBytes(data.sizeBytes || 0)})`,
      });
      refresh();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 429 && detail?.retryAfterMinutes) {
        setBanner({
          kind: "warn",
          text: `Rate limit reached — 6 saves per hour. Next save in ${detail.retryAfterMinutes} min.`,
        });
      } else {
        setBanner({
          kind: "err",
          text: typeof detail === "string"
            ? detail : (detail?.message || e?.message || "Snapshot failed"),
        });
      }
    } finally {
      setSaving(false);
    }
  }, [refresh]);

  const downloadOne = useCallback((name) => {
    window.location.href = `${API_ROOT}/admin/backup/download/${encodeURIComponent(name)}`;
  }, []);

  const downloadCheckpoint = useCallback((rowId) => {
    window.location.href = `${API_ROOT}/admin/backup/pre-destructive/download/${encodeURIComponent(rowId)}`;
  }, []);

  const purgeCheckpoint = useCallback(async (rowId) => {
    try {
      await api.delete(`/admin/backup/pre-destructive/${encodeURIComponent(rowId)}`);
      setBanner({ kind: "ok", text: `Checkpoint deleted.` });
      setPurgeConfirmId(null);
      refresh();
    } catch (e) {
      setBanner({
        kind: "err",
        text: `Failed to delete checkpoint: ${e?.response?.data?.detail || e.message}`,
      });
    }
  }, [refresh]);

  const local = health || {};
  const offsite = health?.offsiteBackup || {};

  return (
    <div data-testid="admin-backups-page">
      <div className="admin-header">
        <div>
          <h1>Backups &amp; Safety</h1>
          <p className="admin-header__sub">
            Local dumps live at <code>/var/backups/mongo</code> · 30-minute cadence · 24-hour retention.
            Download to your laptop for the ultimate &ldquo;outside-the-pod&rdquo; copy.
          </p>
        </div>
        <div style={{ display: "flex", gap: "0.5rem" }}>
          <button
            type="button"
            className="backup-widget__btn"
            data-testid="admin-backups-refresh"
            onClick={refresh}
            disabled={loading}
          >
            {loading ? "Refreshing…" : "Refresh"}
          </button>
          <button
            type="button"
            className="backup-widget__btn backup-widget__btn--primary"
            data-testid="admin-backups-save-now"
            onClick={onSaveNow}
            disabled={saving}
          >
            {saving ? "Saving snapshot…" : "Save Snapshot Now"}
          </button>
        </div>
      </div>

      {banner && (
        <div
          className={`backup-page-banner backup-page-banner--${banner.kind}`}
          data-testid={`admin-backups-banner-${banner.kind}`}
        >
          {banner.text}
        </div>
      )}

      <div className="admin-grid-2">
        <section
          className={`backup-card backup-card--${local.localColor || local.color || "gray"}`}
          data-testid="admin-backups-local-card"
        >
          <h2>Local dumps</h2>
          <ul className="backup-card__stats">
            <li>
              <span>Status</span>
              <strong data-testid="backups-local-status">
                {local.status || "unknown"}
              </strong>
            </li>
            <li>
              <span>Count</span>
              <strong data-testid="backups-local-count">
                {local.backupCount ?? 0}
              </strong>
            </li>
            <li>
              <span>Latest</span>
              <strong data-testid="backups-local-latest">
                {local.lastBackupAt ? formatRelative(local.lastBackupAt) : "—"}
              </strong>
            </li>
            <li>
              <span>Total size</span>
              <strong>{formatBytes(local.backupDirBytes || 0)}</strong>
            </li>
          </ul>
          {local.error && (
            <p className="backup-card__err" data-testid="backups-local-error">
              {local.error}
            </p>
          )}
        </section>

        <section
          className={`backup-card backup-card--${offsite.color || "gray"}`}
          data-testid="admin-backups-offsite-card"
        >
          <h2>Offsite (Tigris)</h2>
          <ul className="backup-card__stats">
            <li>
              <span>Status</span>
              <strong data-testid="backups-offsite-status">
                {offsite.status || "unknown"}
              </strong>
            </li>
            <li>
              <span>Uploads</span>
              <strong data-testid="backups-offsite-count">
                {offsite.uploadCount ?? 0}
              </strong>
            </li>
            <li>
              <span>Latest</span>
              <strong data-testid="backups-offsite-latest">
                {offsite.lastUploadedAt ? formatRelative(offsite.lastUploadedAt) : "—"}
              </strong>
            </li>
            <li>
              <span>Total size</span>
              <strong>{formatBytes(offsite.offsiteBackupBytes || 0)}</strong>
            </li>
          </ul>
          {offsite.lastError && (
            <div
              className="backup-card__err"
              data-testid="backups-offsite-error"
            >
              <strong>Not configured:</strong> {offsite.lastError}. Recommend enabling
              offsite backups for full disaster recovery. See{" "}
              <code>/app/memory/tier_c_support_message.md</code> for context.
            </div>
          )}
        </section>
      </div>

      <section style={{ marginTop: "1.25rem" }}>
        <h2>All local dumps</h2>
        <p className="admin-header__sub">
          Click &ldquo;Download&rdquo; on any row to save a tar.gz to your machine.
        </p>
        <table className="admin-table" data-testid="admin-backups-table">
          <thead>
            <tr>
              <th>Timestamp</th>
              <th>Age</th>
              <th>Size</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && !loading && (
              <tr>
                <td colSpan="4" style={{ color: "var(--wmleye-ink-soft)" }}>
                  No local dumps on disk yet.
                </td>
              </tr>
            )}
            {items.map((it) => (
              <tr key={it.name} data-testid={`backup-row-${it.name}`}>
                <td><code>{it.name}</code><br /><small>{absoluteTimestamp(it.createdAt)}</small></td>
                <td>{formatRelative(it.createdAt)}</td>
                <td>{formatBytes(it.sizeBytes)}</td>
                <td style={{ textAlign: "right" }}>
                  <button
                    type="button"
                    className="backup-widget__btn"
                    data-testid={`backup-row-download-${it.name}`}
                    onClick={() => downloadOne(it.name)}
                  >
                    Download
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section style={{ marginTop: "1.75rem" }} data-testid="admin-backups-predestruct-section">
        <h2>Pre-destructive checkpoints</h2>
        <p className="admin-header__sub">
          Every bulk delete or world/level/hitbox delete auto-fires a fresh
          mongodump BEFORE the write. These checkpoints never auto-prune —
          they live until you manually delete them. If a snapshot ever fails,
          the destructive action is blocked with HTTP 503.
        </p>
        <table className="admin-table" data-testid="admin-backups-predestruct-table">
          <thead>
            <tr>
              <th>Action</th>
              <th>Entity</th>
              <th>Admin</th>
              <th>Requested</th>
              <th>Size</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {checkpoints.length === 0 && !loading && (
              <tr>
                <td colSpan="6" style={{ color: "var(--wmleye-ink-soft)" }}>
                  No destructive events yet. That&rsquo;s a good thing.
                </td>
              </tr>
            )}
            {checkpoints.map((cp) => (
              <tr key={cp.id} data-testid={`predestruct-row-${cp.id}`}>
                <td>
                  <code>{cp.action}</code>
                  {!cp.success && (
                    <div style={{ color: "#a1301d", fontSize: "0.72rem" }} data-testid={`predestruct-row-error-${cp.id}`}>
                      BLOCKED: {cp.error || "snapshot failed"}
                    </div>
                  )}
                </td>
                <td>
                  <small>{cp.entityType}</small><br />
                  <code style={{ fontSize: "0.72rem" }}>{cp.entityId || "—"}</code>
                </td>
                <td><small>{cp.adminEmail || cp.adminUserId || "—"}</small></td>
                <td>
                  {formatRelative(cp.requestedAt)}<br />
                  <small>{absoluteTimestamp(cp.requestedAt)}</small>
                </td>
                <td>{cp.success ? formatBytes(cp.sizeBytes || 0) : "—"}</td>
                <td style={{ textAlign: "right" }}>
                  {cp.success && (
                    <button
                      type="button"
                      className="backup-widget__btn"
                      data-testid={`predestruct-row-download-${cp.id}`}
                      onClick={() => downloadCheckpoint(cp.id)}
                    >
                      Download
                    </button>
                  )}
                  {purgeConfirmId === cp.id ? (
                    <span style={{ marginLeft: "0.4rem", display: "inline-flex", gap: "0.25rem" }}>
                      <button
                        type="button"
                        className="backup-widget__btn backup-widget__btn--primary"
                        style={{ background: "#c8402b", borderColor: "#c8402b" }}
                        data-testid={`predestruct-row-purge-confirm-${cp.id}`}
                        onClick={() => purgeCheckpoint(cp.id)}
                      >
                        Confirm delete
                      </button>
                      <button
                        type="button"
                        className="backup-widget__btn"
                        data-testid={`predestruct-row-purge-cancel-${cp.id}`}
                        onClick={() => setPurgeConfirmId(null)}
                      >
                        Cancel
                      </button>
                    </span>
                  ) : (
                    <button
                      type="button"
                      className="backup-widget__btn"
                      style={{ marginLeft: "0.4rem" }}
                      data-testid={`predestruct-row-purge-${cp.id}`}
                      onClick={() => setPurgeConfirmId(cp.id)}
                    >
                      Delete…
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}
